import React, { useEffect, useState } from "react";
import { api, formatApiError, formatCurrency, formatDate } from "@/lib/api";
import { Receipt, Coins, HourglassMedium, Plus, Eraser } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";

const NOTE_TONE = { credit: "bg-rose-50 text-rose-700", debit: "bg-blue-50 text-blue-700", write_off: "bg-slate-800 text-white" };

// ─────────── Credit / Debit notes + write-off ───────────
function NotesCard({ canEdit, invoices, onChanged }) {
  const [notes, setNotes] = useState([]);
  const [open, setOpen] = useState(false);
  const [woOpen, setWoOpen] = useState(false);
  const [form, setForm] = useState({ invoice_id: "", type: "credit", amount: "", reason: "" });
  const [wo, setWo] = useState({ invoice_id: "", reason: "" });
  const openInvoices = invoices.filter((i) => i.status !== "paid" && i.balance > 0);

  const load = async () => {
    try { const { data } = await api.get("/finance/ar/notes"); setNotes(data.items || []); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    try {
      const { data } = await api.post(`/finance/ar/${form.invoice_id}/credit-note`, {
        type: form.type, amount: parseFloat(form.amount) || 0, reason: form.reason });
      toast.success(`${data.note_no} applied`);
      setOpen(false); setForm({ invoice_id: "", type: "credit", amount: "", reason: "" });
      load(); onChanged();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const writeOff = async () => {
    try {
      const { data } = await api.post(`/finance/ar/${wo.invoice_id}/write-off`, { reason: wo.reason });
      toast.success(`${data.note_no} — balance written off to bad debt`);
      setWoOpen(false); setWo({ invoice_id: "", reason: "" });
      load(); onChanged();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="dc-card overflow-hidden" data-testid="ar-notes-card">
      <div className="border-b border-slate-100 px-5 py-3 flex items-center gap-2">
        <Receipt size={18} weight="fill" style={{ color: "#1E5CBA" }} />
        <h3 className="font-display text-[15px] font-semibold text-slate-900">Credit / Debit Notes & Write-offs</h3>
        {canEdit && (
          <div className="ml-auto flex gap-2">
            <Button size="sm" variant="outline" className="h-7" onClick={() => setWoOpen(true)} data-testid="ar-writeoff-btn"><Eraser size={12} className="mr-1" /> Write off…</Button>
            <Button size="sm" className="btn-primary-blue h-7" onClick={() => setOpen(true)} data-testid="ar-note-add-btn"><Plus size={12} className="mr-1" /> New note</Button>
          </div>
        )}
      </div>
      {notes.length === 0 ? (
        <div className="px-5 py-5 text-sm text-slate-500" data-testid="ar-notes-empty">No credit or debit notes yet.</div>
      ) : (
        <div className="responsive-table-wrap">
          <table className="data-table w-full min-w-[820px]" data-testid="ar-notes-table">
            <thead><tr><th>Note</th><th>Type</th><th>Invoice</th><th>Client</th><th className="text-right">Amount</th><th>Reason</th><th>Date</th></tr></thead>
            <tbody>
              {notes.map((n) => (
                <tr key={n.id} data-testid={`ar-note-row-${n.id}`}>
                  <td className="px-5 py-3 font-mono-alt text-xs font-semibold">{n.note_no}</td>
                  <td><span className={`rounded-full px-2 py-0.5 text-[10px] font-bold uppercase ${NOTE_TONE[n.type]}`}>{n.type.replace("_", "-")}</span></td>
                  <td className="font-mono-alt text-xs">{n.invoice_number}</td>
                  <td className="text-xs">{n.client_name}</td>
                  <td className="text-right font-mono-alt text-xs tabular-nums">{formatCurrency(n.amount)}</td>
                  <td className="text-xs text-slate-500 max-w-[200px] truncate" title={n.reason}>{n.reason}</td>
                  <td className="font-mono-alt text-xs">{formatDate(n.date)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md" data-testid="ar-note-dialog">
          <DialogHeader><DialogTitle>New credit / debit note</DialogTitle>
            <DialogDescription>Credit notes reduce the client's balance (revenue reversed); debit notes increase it. Both post to the GL.</DialogDescription></DialogHeader>
          <div className="space-y-3">
            <div><Label>Invoice</Label>
              <Select value={form.invoice_id || undefined} onValueChange={(v) => setForm({ ...form, invoice_id: v })}>
                <SelectTrigger data-testid="ar-note-invoice"><SelectValue placeholder="Choose invoice…" /></SelectTrigger>
                <SelectContent>{openInvoices.map((i) => <SelectItem key={i.id} value={i.id}>{i.invoice_number} · {i.client_name} · bal {formatCurrency(i.balance)}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Type</Label>
                <Select value={form.type} onValueChange={(v) => setForm({ ...form, type: v })}>
                  <SelectTrigger data-testid="ar-note-type"><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="credit">Credit note</SelectItem><SelectItem value="debit">Debit note</SelectItem></SelectContent>
                </Select>
              </div>
              <div><Label>Amount ($)</Label><Input type="number" min="0" step="0.01" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} data-testid="ar-note-amount" /></div>
            </div>
            <div><Label>Reason</Label><Textarea rows={2} value={form.reason} onChange={(e) => setForm({ ...form, reason: e.target.value })} data-testid="ar-note-reason" /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button className="btn-primary-blue" onClick={save} disabled={!form.invoice_id || !form.amount || !form.reason.trim()} data-testid="ar-note-save">Apply note</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <Dialog open={woOpen} onOpenChange={setWoOpen}>
        <DialogContent className="max-w-md" data-testid="ar-writeoff-dialog">
          <DialogHeader><DialogTitle>Write off invoice balance</DialogTitle>
            <DialogDescription>Maker-checker: the invoice creator cannot approve its own write-off. Posts to bad-debt expense.</DialogDescription></DialogHeader>
          <div className="space-y-3">
            <div><Label>Invoice</Label>
              <Select value={wo.invoice_id || undefined} onValueChange={(v) => setWo({ ...wo, invoice_id: v })}>
                <SelectTrigger data-testid="ar-writeoff-invoice"><SelectValue placeholder="Choose invoice…" /></SelectTrigger>
                <SelectContent>{openInvoices.map((i) => <SelectItem key={i.id} value={i.id}>{i.invoice_number} · {i.client_name} · bal {formatCurrency(i.balance)}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Reason</Label><Textarea rows={2} value={wo.reason} onChange={(e) => setWo({ ...wo, reason: e.target.value })} data-testid="ar-writeoff-reason" /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setWoOpen(false)}>Cancel</Button>
            <Button variant="destructive" onClick={writeOff} disabled={!wo.invoice_id || !wo.reason.trim()} data-testid="ar-writeoff-save">Write off</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ─────────── Unallocated receipts ───────────
function ReceiptsCard({ canEdit, invoices, onChanged }) {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [allocFor, setAllocFor] = useState(null);
  const [form, setForm] = useState({ client_name: "", date: new Date().toISOString().slice(0, 10), amount: "", bank_account: "Premier", reference: "" });
  const [allocs, setAllocs] = useState([{ invoice_id: "", amount: "" }]);
  const openInvoices = invoices.filter((i) => i.status !== "paid" && i.balance > 0);

  const load = async () => {
    try { const { data } = await api.get("/finance/ar/receipts"); setItems(data.items || []); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    try {
      const { data } = await api.post("/finance/ar/receipts", { ...form, amount: parseFloat(form.amount) || 0 });
      toast.success(`${data.receipt_no} recorded — allocate it to invoices when identified`);
      setOpen(false); setForm({ client_name: "", date: new Date().toISOString().slice(0, 10), amount: "", bank_account: "Premier", reference: "" });
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const allocate = async () => {
    try {
      await api.post(`/finance/ar/receipts/${allocFor.id}/allocate`, {
        allocations: allocs.filter((a) => a.invoice_id && a.amount).map((a) => ({ invoice_id: a.invoice_id, amount: parseFloat(a.amount) || 0 })) });
      toast.success("Receipt allocated");
      setAllocFor(null); setAllocs([{ invoice_id: "", amount: "" }]);
      load(); onChanged();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="dc-card overflow-hidden" data-testid="ar-receipts-card">
      <div className="border-b border-slate-100 px-5 py-3 flex items-center gap-2">
        <Coins size={18} weight="fill" className="text-emerald-600" />
        <h3 className="font-display text-[15px] font-semibold text-slate-900">Unallocated Receipts & Payment Allocation</h3>
        {canEdit && (
          <Button size="sm" className="btn-primary-blue ml-auto h-7" onClick={() => setOpen(true)} data-testid="ar-receipt-add-btn"><Plus size={12} className="mr-1" /> Record receipt</Button>
        )}
      </div>
      {items.length === 0 ? (
        <div className="px-5 py-5 text-sm text-slate-500" data-testid="ar-receipts-empty">No receipts recorded. Use this when money arrives before you know which invoice it settles.</div>
      ) : (
        <div className="responsive-table-wrap">
          <table className="data-table w-full min-w-[820px]" data-testid="ar-receipts-table">
            <thead><tr><th>Receipt</th><th>Client</th><th>Date</th><th className="text-right">Amount</th><th className="text-right">Unallocated</th><th>Status</th><th className="w-28">Actions</th></tr></thead>
            <tbody>
              {items.map((r) => (
                <tr key={r.id} data-testid={`ar-receipt-row-${r.id}`}>
                  <td className="px-5 py-3 font-mono-alt text-xs font-semibold">{r.receipt_no}</td>
                  <td className="text-xs">{r.client_name}</td>
                  <td className="font-mono-alt text-xs">{formatDate(r.date)}</td>
                  <td className="text-right font-mono-alt text-xs tabular-nums">{formatCurrency(r.amount)}</td>
                  <td className="text-right font-mono-alt text-xs tabular-nums font-semibold">{formatCurrency(r.unallocated_amount)}</td>
                  <td><span className={`rounded-full px-2 py-0.5 text-[11px] font-semibold ${r.status === "allocated" ? "bg-emerald-50 text-emerald-700" : r.status === "partially_allocated" ? "bg-amber-50 text-amber-700" : "bg-slate-100 text-slate-600"}`}>{r.status.replace("_", " ").toUpperCase()}</span></td>
                  <td>
                    {canEdit && r.unallocated_amount > 0.005 && (
                      <Button variant="outline" size="sm" className="h-7" onClick={() => { setAllocFor(r); setAllocs([{ invoice_id: "", amount: "" }]); }} data-testid={`ar-receipt-allocate-${r.id}`}>Allocate</Button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md" data-testid="ar-receipt-dialog">
          <DialogHeader><DialogTitle>Record unallocated receipt</DialogTitle>
            <DialogDescription>Cash is banked immediately (DR bank / CR AR); allocate to invoices once identified.</DialogDescription></DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2"><Label>Client</Label><Input value={form.client_name} onChange={(e) => setForm({ ...form, client_name: e.target.value })} data-testid="ar-receipt-client" /></div>
            <div><Label>Date</Label><Input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} /></div>
            <div><Label>Amount ($)</Label><Input type="number" min="0" step="0.01" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} data-testid="ar-receipt-amount" /></div>
            <div><Label>Bank</Label><Input value={form.bank_account} onChange={(e) => setForm({ ...form, bank_account: e.target.value })} /></div>
            <div><Label>Reference</Label><Input value={form.reference} onChange={(e) => setForm({ ...form, reference: e.target.value })} /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button className="btn-primary-blue" onClick={save} disabled={!form.client_name || !form.amount} data-testid="ar-receipt-save">Record</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <Dialog open={!!allocFor} onOpenChange={(v) => !v && setAllocFor(null)}>
        <DialogContent className="max-w-lg" data-testid="ar-alloc-dialog">
          <DialogHeader><DialogTitle>Allocate {allocFor?.receipt_no}</DialogTitle>
            <DialogDescription>Unallocated: {formatCurrency(allocFor?.unallocated_amount || 0)}</DialogDescription></DialogHeader>
          <div className="space-y-2">
            {allocs.map((a, i) => (
              <div key={i} className="grid grid-cols-[1fr_130px] gap-2">
                <Select value={a.invoice_id || undefined} onValueChange={(v) => setAllocs((ls) => ls.map((x, idx) => idx === i ? { ...x, invoice_id: v } : x))}>
                  <SelectTrigger className="h-9" data-testid={`ar-alloc-invoice-${i}`}><SelectValue placeholder="Invoice…" /></SelectTrigger>
                  <SelectContent>{openInvoices.map((inv) => <SelectItem key={inv.id} value={inv.id}>{inv.invoice_number} · bal {formatCurrency(inv.balance)}</SelectItem>)}</SelectContent>
                </Select>
                <Input type="number" min="0" step="0.01" placeholder="Amount" value={a.amount} onChange={(e) => setAllocs((ls) => ls.map((x, idx) => idx === i ? { ...x, amount: e.target.value } : x))} data-testid={`ar-alloc-amount-${i}`} />
              </div>
            ))}
            <Button variant="outline" size="sm" onClick={() => setAllocs((ls) => [...ls, { invoice_id: "", amount: "" }])}><Plus size={12} className="mr-1" /> Add line</Button>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAllocFor(null)}>Cancel</Button>
            <Button className="btn-primary-blue" onClick={allocate} data-testid="ar-alloc-save">Allocate</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ─────────── Revenue recognition ───────────
function RevRecCard({ canEdit, invoices, onChanged }) {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ invoice_id: "", months: 3, start_period: new Date().toISOString().slice(0, 7) });
  const deferredInvoices = invoices.filter((i) => i.revenue_treatment === "deferred");

  const load = async () => {
    try { const { data } = await api.get("/finance/ar/recognition-schedules"); setItems(data.items || []); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    try {
      await api.post(`/finance/ar/${form.invoice_id}/recognition-schedule`, {
        method: "straight_line", months: parseInt(form.months) || 3, start_period: form.start_period });
      toast.success("Recognition schedule created");
      setOpen(false); load();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const recognize = async (sch) => {
    const next = (sch.entries || []).find((e) => !e.recognized);
    if (!next) return;
    try {
      const { data } = await api.post(`/finance/ar/recognition-schedules/${sch.id}/recognize`, { entry_index: next.index });
      toast.success(`${next.label} recognized (${data.journal_entry_no || "journal posted"})`);
      load(); onChanged();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="dc-card overflow-hidden" data-testid="ar-revrec-card">
      <div className="border-b border-slate-100 px-5 py-3 flex items-center gap-2">
        <HourglassMedium size={18} weight="fill" className="text-indigo-600" />
        <h3 className="font-display text-[15px] font-semibold text-slate-900">Revenue Recognition (Deferred Income)</h3>
        <span className="font-mono-alt text-[10px] uppercase tracking-widest text-slate-500">Invoiced ≠ earned — release 2150 → 4110 per schedule</span>
        {canEdit && (
          <Button size="sm" className="btn-primary-blue ml-auto h-7" onClick={() => setOpen(true)} data-testid="revrec-add-btn"><Plus size={12} className="mr-1" /> Schedule</Button>
        )}
      </div>
      {items.length === 0 ? (
        <div className="px-5 py-5 text-sm text-slate-500" data-testid="revrec-empty">
          No schedules. Create AR invoices with <b>deferred</b> revenue treatment, then schedule recognition here.
          {deferredInvoices.length > 0 && ` (${deferredInvoices.length} deferred invoice(s) waiting)`}
        </div>
      ) : (
        <div className="responsive-table-wrap">
          <table className="data-table w-full min-w-[860px]" data-testid="revrec-table">
            <thead><tr><th>Invoice</th><th>Client</th><th>Method</th><th className="text-right">Total</th><th className="text-right">Recognized</th><th>Progress</th><th className="w-40">Actions</th></tr></thead>
            <tbody>
              {items.map((s) => {
                const done = (s.entries || []).filter((e) => e.recognized).length;
                return (
                  <tr key={s.id} data-testid={`revrec-row-${s.id}`}>
                    <td className="px-5 py-3 font-mono-alt text-xs font-semibold">{s.invoice_number}</td>
                    <td className="text-xs">{s.client_name}</td>
                    <td className="text-xs uppercase tracking-wider text-slate-500">{s.method.replace("_", " ")}</td>
                    <td className="text-right font-mono-alt text-xs tabular-nums">{formatCurrency(s.total)}</td>
                    <td className="text-right font-mono-alt text-xs tabular-nums font-semibold">{formatCurrency(s.recognized_total)}</td>
                    <td className="text-xs font-mono-alt">{done}/{(s.entries || []).length} {s.status === "completed" && <span className="text-emerald-600 font-bold">✓</span>}</td>
                    <td>
                      {canEdit && s.status !== "completed" && (
                        <Button variant="outline" size="sm" className="h-7" onClick={() => recognize(s)} data-testid={`revrec-recognize-${s.id}`}>Recognize next</Button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md" data-testid="revrec-dialog">
          <DialogHeader><DialogTitle>New recognition schedule</DialogTitle>
            <DialogDescription>Straight-line release of deferred revenue over N months.</DialogDescription></DialogHeader>
          <div className="space-y-3">
            <div><Label>Deferred invoice</Label>
              <Select value={form.invoice_id || undefined} onValueChange={(v) => setForm({ ...form, invoice_id: v })}>
                <SelectTrigger data-testid="revrec-invoice"><SelectValue placeholder="Choose deferred invoice…" /></SelectTrigger>
                <SelectContent>
                  {deferredInvoices.length === 0
                    ? <SelectItem value="__none" disabled>No deferred invoices — create one in the AR table above</SelectItem>
                    : deferredInvoices.map((i) => <SelectItem key={i.id} value={i.id}>{i.invoice_number} · {i.client_name} · {formatCurrency(i.total)}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Months</Label><Input type="number" min="2" max="36" value={form.months} onChange={(e) => setForm({ ...form, months: e.target.value })} data-testid="revrec-months" /></div>
              <div><Label>Start period</Label><Input type="month" value={form.start_period} onChange={(e) => setForm({ ...form, start_period: e.target.value })} data-testid="revrec-start" /></div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button className="btn-primary-blue" onClick={save} disabled={!form.invoice_id} data-testid="revrec-save">Create schedule</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default function ARAdvancedSection({ canEdit, invoices, onChanged }) {
  return (
    <div className="space-y-5" data-testid="ar-advanced-section">
      <NotesCard canEdit={canEdit} invoices={invoices} onChanged={onChanged} />
      <ReceiptsCard canEdit={canEdit} invoices={invoices} onChanged={onChanged} />
      <RevRecCard canEdit={canEdit} invoices={invoices} onChanged={onChanged} />
    </div>
  );
}
